import React, { useState, useMemo, useEffect } from 'react';
import './RecommendationsSelector.scss';
import { getIcon } from '@flybits/design-system';
import { ChartConfigState, RecommendedMetric } from 'pages/AnalyticsV2/types';
import { ReactComponent as IconPushSent } from 'pages/AnalyticsV2/CustomAnalytics/icons/push-sent.svg';
import { ReactComponent as IconPushOpened } from 'pages/AnalyticsV2/CustomAnalytics/icons/push-opened.svg';
import { ReactComponent as IconPushEngagement } from 'pages/AnalyticsV2/CustomAnalytics/icons/push-engagement.svg';
import { ReactComponent as IconContentClicked } from 'pages/AnalyticsV2/CustomAnalytics/icons/content-clicked.svg';
import { ReactComponent as IconExperienceEngagement } from 'pages/AnalyticsV2/CustomAnalytics/icons/experience-engagement.svg';
import { ReactComponent as IconEmailSent } from 'pages/AnalyticsV2/CustomAnalytics/icons/email-sent.svg';
import { ReactComponent as IconEmailBounced } from 'pages/AnalyticsV2/CustomAnalytics/icons/email-bounced.svg';
import { ReactComponent as IconEmailClicked } from 'pages/AnalyticsV2/CustomAnalytics/icons/email-clicked.svg';
import { ReactComponent as IconSmsSent } from 'pages/AnalyticsV2/CustomAnalytics/icons/sms-sent.svg';
import { ReactComponent as IconSmsDelivered } from 'pages/AnalyticsV2/CustomAnalytics/icons/sms-delivered.svg';
import { ReactComponent as IconSmsFailed } from 'pages/AnalyticsV2/CustomAnalytics/icons/sms-failed.svg';
import { VISUALIZER_OPTIONS } from 'pages/AnalyticsV2/CustomAnalytics/CreateOrEditChart/constants';

const RECOMMENDED_METRICS: RecommendedMetric[] = [
  {
    icon: <IconPushSent />,
    metric: { key: 'push-sent', name: 'push notifications sent' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconPushOpened />,
    metric: { key: 'push-viewed', name: 'push notifications viewed' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconPushEngagement />,
    metric: { key: 'push-engaged', name: 'push engagement rate' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconContentClicked />,
    metric: { key: 'content-viewed', name: 'content viewed' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconExperienceEngagement />,
    metric: { key: 'content-engaged', name: 'total content engagement' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
];

const EMAIL_RECOMMENDED_METRICS: RecommendedMetric[] = [
  {
    icon: <IconEmailSent />,
    metric: { key: 'email-sent', name: 'emails sent' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconEmailBounced />,
    metric: { key: 'email-bounced', name: 'email bounce rate' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconEmailClicked />,
    metric: { key: 'email-clicked', name: 'email click rate' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
];

const SMS_RECOMMENDED_METRICS: RecommendedMetric[] = [
  {
    icon: <IconSmsSent />,
    metric: { key: 'sms-sent', name: 'SMS sent' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconSmsDelivered />,
    metric: { key: 'sms-delivered', name: 'SMS delivered' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
  {
    icon: <IconSmsFailed />,
    metric: { key: 'sms-failed', name: 'SMS failed' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.BAR,
  },
];

const MAIN_CLASS = 'recommendation-selector';
const CLASSES = {
  INFO: `${MAIN_CLASS}__info`,
  LIST: `${MAIN_CLASS}__list`,
  ITEM: `${MAIN_CLASS}__item`,
};
interface RecommendationsSelectorProps {
  onChange: (recommendation: RecommendedMetric) => void;
  chartConfig?: ChartConfigState;
  isEmailSmsOptionEnabled?: boolean;
}

const RecommendedItem: React.FC<{
  selected?: boolean;
  icon: JSX.Element;
  metric: { key: string; name: string };
  time: { period: number; interval: string };
  onClick: () => void;
}> = ({ selected, icon, metric, time, onClick }) => {
  return (
    <button onClick={onClick} className={`${CLASSES.ITEM} ${selected ? 'selected' : ''}`}>
      {icon} For the selected experience, show me <span>{`${metric.name}`}</span> in the past{' '}
      <span>{`${time.period} ${time.interval}s`}</span>
    </button>
  );
};
export default function RecommendationsSelector({
  onChange,
  chartConfig,
  isEmailSmsOptionEnabled,
}: RecommendationsSelectorProps) {
  const [selected, setSelected] = useState(0);

  // TODO: This logic currently separates existing metrics from Email/SMS metrics manually (introduced in PET-5699).
  // The actual list of recommended metrics options should be derived dynamically from the selected experience and its available triggers and actions.
  // A spike ticket (PET-5857) exists to analyze and refactor this to remove hardcoded logic.
  const currentRecommendedMetrics = useMemo(() => {
    if (!isEmailSmsOptionEnabled) {
      return RECOMMENDED_METRICS;
    }
    const experience = chartConfig?.selectedExperiences?.[0];

    if (experience?.hasEmail && experience?.hasSMS) {
      return [...EMAIL_RECOMMENDED_METRICS, ...SMS_RECOMMENDED_METRICS];
    }

    if (experience?.hasEmail) {
      return EMAIL_RECOMMENDED_METRICS;
    }

    if (experience?.hasSMS) {
      return SMS_RECOMMENDED_METRICS;
    }

    return RECOMMENDED_METRICS;
  }, [chartConfig?.selectedExperiences, isEmailSmsOptionEnabled]);

  useEffect(() => {
    setSelected(0);
    onChange(currentRecommendedMetrics[0]);
  }, [currentRecommendedMetrics, onChange]);

  const handleClick = (selectedIndex: number) => {
    onChange(currentRecommendedMetrics[selectedIndex]);
    setSelected(selectedIndex);
  };
  const showExperienceHint = !chartConfig?.selectedExperiences?.length;

  return (
    <section className={MAIN_CLASS}>
      {showExperienceHint && (
        <div className={CLASSES.INFO}>
          {getIcon('error', {})} In order to utilize recommendations make sure you have an experience selected
        </div>
      )}
      <ul className={CLASSES.LIST}>
        {currentRecommendedMetrics.map((metric, i) => (
          <RecommendedItem key={i} selected={i === selected} {...metric} onClick={() => handleClick(i)} />
        ))}
      </ul>
    </section>
  );
}
